/** What one filing covers of its group - **ADR-0224**, the choice and the words for it.
 *
 * A filing is its own company's sites, or also the sites of every subsidiary the ownership record
 * marks included. Nothing here decides which: the server stores the choice per filing, refuses what
 * the filing cannot follow, and names whoever is left out in the issued record. This module is the
 * write and the two labels, so no screen words a choice differently from another.
 *
 * Every sentence a refusal shows is the server's (design rule 7); this decides only *which kind* of
 * refusal it was, because the remedies differ (reload, confirm a restatement, change the period's
 * approach) and a screen holding only a string cannot tell them apart.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom, Refused } from "./refusal";
import type { components } from "./schema";

export type Consolidation = "own_sites" | "subsidiaries";
export type ConsolidationResult = components["schemas"]["ConsolidationOut"];

/** The two choices, as a person reads them. One list, so the radios and the sentence agree. */
export const CONSOLIDATIONS: readonly { value: Consolidation; label: string }[] = [
  { value: "own_sites", label: "This company's own sites" },
  { value: "subsidiaries", label: "Also its included subsidiaries" },
];

export function consolidationLabel(value: string): string {
  return CONSOLIDATIONS.find((option) => option.value === value)?.label ?? "Not stated";
}

export function isConsolidation(value: string): value is Consolidation {
  return value === "own_sites" || value === "subsidiaries";
}

/** The kinds of refusal a change can meet, as a screen branches on them. */
export type BoundaryRefusalKind =
  | "stale_version"
  | "consolidation_unavailable"
  | "consolidation_unresolvable"
  | "issued_acknowledgement_required"
  | "forbidden"
  | "other";

export function boundaryRefusalKind(error: unknown): BoundaryRefusalKind {
  if (!(error instanceof Refused)) return "other";
  if (error.status === 403) return "forbidden";
  switch (error.code) {
    case "stale_version":
    case "consolidation_unavailable":
    case "consolidation_unresolvable":
    case "issued_acknowledgement_required":
      return error.code;
    default:
      return "other";
  }
}

/** Choose what the filing covers. `expectedEdition` is the edition the form was opened against
 * (`context.consolidation_edition`); `acknowledgeIssued` is sent true only when the person ticked
 * that an issued filing will stay as filed and stop matching today's figures. */
export function useChooseConsolidation(obligationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      consolidation: Consolidation;
      expectedEdition: number;
      acknowledgeIssued: boolean;
    }): Promise<ConsolidationResult> => {
      const { data, error, response } = await api.PUT(
        "/api/v1/reports/obligations/{obligation_id}/consolidation",
        {
          params: { path: { obligation_id: obligationId } },
          body: {
            consolidation: input.consolidation,
            expected_edition: input.expectedEdition,
            acknowledge_issued: input.acknowledgeIssued,
          },
        },
      );
      if (error || !data) {
        throw refusalFrom(response, error, "What this filing covers could not be changed.");
      }
      return data;
    },
    onSuccess: async () => {
      // Everything: the choice moves this filing's figures, checks, path and sign-offs, and the
      // Home and Filings lists read them. Naming keys is a list to keep in step with every screen.
      await queryClient.invalidateQueries();
    },
    // A stale form has nothing wrong with it but its age: re-read what the choice now is.
    onError: async (error) => {
      if (boundaryRefusalKind(error) === "stale_version") await queryClient.invalidateQueries();
    },
  });
}
